import React, { Component, CSSProperties } from 'react';
import * as monaco from 'monaco-editor';
import { setCustomTokenizer } from './tokenization';
import { defineCustomThemes } from './themes';
import ResizeObserver from 'resize-observer-polyfill';
import debounce from 'lodash.debounce';

type Props = {
	onChange?: (value: string) => void;

	path?: string;
	value?: string;
	language?: string;

	className: string;

	theme: string;
	editorOptions: monaco.editor.IEditorOptions;
	textOptions: monaco.editor.ITextModelUpdateOptions;
};

export default class Monaco extends Component<Props> {
	private editor?: monaco.editor.IStandaloneCodeEditor;
	private nodeRef = React.createRef<HTMLDivElement>();
	private subscription?: monaco.IDisposable;

	private _resizeObserver = new ResizeObserver((entries, observer) => this.handleResize());

	static defaultProps = {
		width: '100%',
		height: '100%',
		updateCode: false,
	};

	getOptions(): monaco.editor.IStandaloneEditorConstructionOptions {
		return {
			...this.props.editorOptions,
			theme: this.props.theme,
		};
	}

	componentDidMount() {
		if (!this.nodeRef.current) return;
		this._resizeObserver.observe(this.nodeRef.current);

		const { path, value, language } = this.props;
		if (language) {
			setCustomTokenizer(language);
		}
		defineCustomThemes();

		monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
			experimentalDecorators: true,
			allowNonTsExtensions: true,
		});
		monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
			noSemanticValidation: true,
		});

		this.editor = monaco.editor.create(this.nodeRef.current, this.getOptions());

		this.updateModel(value, language, path);
	}

	componentDidUpdate(prevProps: Props) {
		const { path, value, language } = this.props;
		if (prevProps.language !== language && language) {
			setCustomTokenizer(language);
		}

		if (!this.editor) return;

		this.editor.updateOptions(this.getOptions());
		const model = this.editor.getModel();
		if (!model) return;

		if (model.getValue() !== value || path !== prevProps.path) {
			this.updateModel(value, language, path);
		}
	}

	updateModel(value: string = '', language: string | undefined, path?: string) {
		if (!this.editor) return;

		if (path === undefined) {
			// this.editor.setModel(null); //might be useful?
			return;
		}

		const monacoFileId = monaco.Uri.file(path);

		const model =
			this.reuseExistingModel(value, language, monacoFileId) ||
			monaco.editor.createModel(value, language, monacoFileId);

		if (this.props.textOptions) {
			model.updateOptions(this.props.textOptions);
		}

		this.editor.setModel(model);

		if (this.subscription) this.subscription.dispose();
		this.subscription = model.onDidChangeContent(this.handleModelChanges);
	}

	reuseExistingModel(value: string, language: string | undefined, uri: monaco.Uri) {
		const model = monaco.editor.getModel(uri);
		if (!model) return undefined;

		if (value !== model.getValue()) {
			model.setValue(value);
		}

		if (language && model.getModeId() !== language) {
			monaco.editor.setModelLanguage(model, language);
		}

		return model;
	}

	handleResize = debounce(this._handleResize, 180, {
		leading: true,
	});

	private _handleResize(): void {
		if (!this.nodeRef.current || !this.editor) return;
		const rect = this.nodeRef.current.getBoundingClientRect();
		const { width, height } = rect;

		this.editor.layout({ height, width });
	}

	handleModelChanges = () => {
		if (!this.editor) return;
		if (!this.props.onChange) return;
		const model = this.editor.getModel();
		if (!model) return;

		const value = model.getValue();
		this.props.onChange(value);
	};

	componentWillUnmount() {
		this.editor && this.editor.dispose();
		if (this.nodeRef.current) {
			this._resizeObserver.unobserve(this.nodeRef.current);
		}
	}

	render() {
		return <div ref={this.nodeRef} className={this.props.className} />;
	}
}
